/**
 * Health Insight Generator
 * Generates simple, actionable insights based on Quick Health Check inputs
 * All insights are non-clinical and evidence-informed
 */

// RED FLAG PATTERNS - High-risk symptoms requiring urgent medical attention
const RED_FLAG_PATTERNS = {
  // Cardiac/respiratory emergencies
  chest_pain_breathless: {
    condition: (data) => {
      const symptom = data.additional_symptom?.toLowerCase() || '';
      return (symptom.includes('chest pain') || symptom.includes('chest') && symptom.includes('pain')) &&
             (symptom.includes('breathless') || symptom.includes('shortness of breath') || symptom.includes('breathing'));
    },
    severity: 'severe',
    message: 'Chest pain with difficulty breathing requires immediate medical assessment'
  },
  
  chest_pain_severe: {
    condition: (data) => {
      const symptom = data.additional_symptom?.toLowerCase() || '';
      return (symptom.includes('severe chest pain') || symptom.includes('crushing') || symptom.includes('tight chest'));
    },
    severity: 'severe',
    message: 'Severe chest pain should be evaluated urgently'
  },

  // Neurological emergencies
  severe_dizziness_fainting: {
    condition: (data) => {
      const symptom = data.additional_symptom?.toLowerCase() || '';
      return (symptom.includes('severe dizziness') || symptom.includes('very dizzy')) &&
             (symptom.includes('faint') || symptom.includes('collapse') || symptom.includes('passing out'));
    },
    severity: 'severe',
    message: 'Severe dizziness with fainting episodes needs prompt medical attention'
  },

  severe_headache_sudden: {
    condition: (data) => {
      const symptom = data.additional_symptom?.toLowerCase() || '';
      return symptom.includes('severe headache') || symptom.includes('worst headache') || 
             symptom.includes('sudden headache') || symptom.includes('thunderclap');
    },
    severity: 'severe',
    message: 'Sudden or severe headaches should be assessed by a healthcare provider'
  },

  // Breathing difficulties
  difficulty_breathing: {
    condition: (data) => {
      const symptom = data.additional_symptom?.toLowerCase() || '';
      return symptom.includes('can\'t breathe') || symptom.includes('difficulty breathing') ||
             symptom.includes('gasping') || symptom.includes('severe breathless');
    },
    severity: 'severe',
    message: 'Difficulty breathing requires immediate medical assessment'
  },

  // Other critical symptoms
  severe_bleeding: {
    condition: (data) => {
      const symptom = data.additional_symptom?.toLowerCase() || '';
      return symptom.includes('severe bleeding') || symptom.includes('heavy bleeding') ||
             symptom.includes('bleeding won\'t stop');
    },
    severity: 'severe',
    message: 'Uncontrolled bleeding needs urgent medical attention'
  },

  confusion_consciousness: {
    condition: (data) => {
      const symptom = data.additional_symptom?.toLowerCase() || '';
      return symptom.includes('confused') || symptom.includes('disoriented') ||
             symptom.includes('consciousness') || symptom.includes('unresponsive');
    },
    severity: 'severe',
    message: 'Confusion or changes in consciousness require immediate evaluation'
  }
};

// Insight patterns based on user inputs
const INSIGHT_PATTERNS = {
  // Fatigue patterns
  tired_no_sleep: {
    condition: (data) => data.feeling === 'tired' && data.habits.sleep === false,
    insight: "Your tiredness may be linked to not getting enough rest",
    severity: 'medium',
    icon: '😴'
  },
  tired_good_sleep: {
    condition: (data) => data.feeling === 'tired' && data.habits.sleep === true,
    insight: "You're feeling tired despite sleeping well - your body may need extra rest today",
    severity: 'low',
    icon: '🌙'
  },
  
  // Hydration patterns
  no_water: {
    condition: (data) => data.habits.water === false,
    insight: "Not drinking enough water today can affect your energy and focus",
    severity: 'medium',
    icon: '💧'
  },
  headache_no_water: {
    condition: (data) => data.feeling === 'headache' && data.habits.water === false,
    insight: "Your headache might be related to not drinking enough water",
    severity: 'high',
    icon: '🤕'
  },
  
  // Stress patterns
  stressed_no_movement: {
    condition: (data) => data.feeling === 'stressed' && data.habits.movement === false,
    insight: "Stress can build up when we're not moving our body",
    severity: 'medium',
    icon: '😰'
  },
  stressed_no_sleep: {
    condition: (data) => data.feeling === 'stressed' && data.habits.sleep === false,
    insight: "Lack of sleep can make stress feel worse",
    severity: 'high',
    icon: '😰'
  },
  
  // Nutrition patterns
  no_food: {
    condition: (data) => data.habits.food === false,
    insight: "Not eating well today may be affecting your energy levels",
    severity: 'medium',
    icon: '🍎'
  },
  tired_no_food: {
    condition: (data) => data.feeling === 'tired' && data.habits.food === false,
    insight: "Your tiredness could be linked to not eating well today",
    severity: 'high',
    icon: '😴'
  },
  
  // Positive patterns
  all_good: {
    condition: (data) => {
      const goodHabits = Object.values(data.habits).filter(v => v === true).length;
      return data.feeling === 'good' && goodHabits >= 3;
    },
    insight: "You're doing great! Your healthy habits are supporting your wellbeing",
    severity: 'positive',
    icon: '✨'
  },
  
  // Multiple issues
  multiple_issues: {
    condition: (data) => {
      const badHabits = Object.values(data.habits).filter(v => v === false).length;
      return badHabits >= 3 && ['tired', 'headache', 'stressed'].includes(data.feeling);
    },
    insight: "Several factors might be affecting how you feel today",
    severity: 'high',
    icon: '⚠️'
  }
};

// Action recommendations based on insights
const ACTIONS = {
  // Hydration actions
  hydration: {
    conditions: ['no_water', 'headache_no_water'],
    actions: [
      { text: 'Drink 2 glasses of water now', icon: '💧', priority: 1 },
      { text: 'Keep water nearby for the next hour', icon: '🥤', priority: 2 }
    ]
  },
  
  // Rest actions
  rest: {
    conditions: ['tired_no_sleep', 'tired_good_sleep', 'stressed_no_sleep'],
    actions: [
      { text: 'Rest for 20-30 minutes', icon: '🛋️', priority: 1 },
      { text: 'Try a 10-minute breathing exercise', icon: '🧘', priority: 2 },
      { text: 'Go to bed 30 minutes earlier tonight', icon: '🌙', priority: 3 }
    ]
  },
  
  // Movement actions
  movement: {
    conditions: ['stressed_no_movement', 'tired_no_food'],
    actions: [
      { text: 'Take a 10-minute walk outside', icon: '🚶', priority: 1 },
      { text: 'Do gentle stretches for 5 minutes', icon: '🤸', priority: 2 }
    ]
  },
  
  // Nutrition actions
  nutrition: {
    conditions: ['no_food', 'tired_no_food'],
    actions: [
      { text: 'Have a healthy snack or meal soon', icon: '🍎', priority: 1 },
      { text: 'Plan your meals for the rest of the day', icon: '📋', priority: 2 }
    ]
  },
  
  // Stress management
  stress: {
    conditions: ['stressed_no_movement', 'stressed_no_sleep'],
    actions: [
      { text: 'Reduce screen time for the next hour', icon: '📱', priority: 1 },
      { text: 'Talk to someone you trust', icon: '💬', priority: 2 },
      { text: 'Write down what\'s on your mind', icon: '📝', priority: 3 }
    ]
  },
  
  // General wellness
  general: {
    conditions: ['all_good'],
    actions: [
      { text: 'Keep up your healthy habits', icon: '✨', priority: 1 },
      { text: 'Share what\'s working with a friend', icon: '🤝', priority: 2 }
    ]
  }
};

// When to seek help guidelines
const SEEK_HELP_GUIDELINES = {
  tired: [
    "If fatigue continues for more than 3 days",
    "If you feel exhausted even after sleeping"
  ],
  headache: [
    "If headache is severe or sudden",
    "If headache continues despite rest and water",
    "If you have other symptoms like vision changes"
  ],
  stressed: [
    "If stress is affecting your daily life",
    "If you feel overwhelmed most days",
    "If you're having trouble sleeping regularly"
  ],
  general: [
    "If symptoms continue for more than 3 days",
    "If symptoms get worse",
    "If new symptoms appear",
    "If you're concerned about your health"
  ]
};

/**
 * Generate personalized health insights
 * @param {Object} checkInData - Quick check-in data
 * @returns {Object} - Insights, actions, guidelines, and red flag alert
 */
export const generateHealthInsights = (checkInData) => {
  const insights = [];
  const matchedConditions = [];
  
  // CHECK FOR RED FLAGS FIRST
  let redFlagAlert = null;
  for (const [key, pattern] of Object.entries(RED_FLAG_PATTERNS)) {
    if (pattern.condition(checkInData)) {
      redFlagAlert = {
        triggered: true,
        severity: pattern.severity,
        message: pattern.message,
        pattern: key
      };
      break; // Stop at first red flag
    }
  }
  
  // Find matching insight patterns
  Object.entries(INSIGHT_PATTERNS).forEach(([key, pattern]) => {
    if (pattern.condition(checkInData)) {
      insights.push({
        key,
        text: pattern.insight,
        severity: pattern.severity,
        icon: pattern.icon
      });
      matchedConditions.push(key);
    }
  });
  
  // Limit to 2 most relevant insights (highest severity first)
  const severityOrder = { high: 0, medium: 1, low: 2, positive: 3 };
  const topInsights = insights
    .sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity])
    .slice(0, 2);
  
  // Generate recommended actions
  const recommendedActions = [];
  Object.entries(ACTIONS).forEach(([category, config]) => {
    const hasMatchingCondition = config.conditions.some(c => matchedConditions.includes(c));
    if (hasMatchingCondition) {
      recommendedActions.push(...config.actions);
    }
  });
  
  // Remove duplicates and sort by priority
  const uniqueActions = Array.from(
    new Map(recommendedActions.map(a => [a.text, a])).values()
  ).sort((a, b) => a.priority - b.priority).slice(0, 4);
  
  // Get seek help guidelines
  const seekHelpGuidelines = SEEK_HELP_GUIDELINES[checkInData.feeling] || SEEK_HELP_GUIDELINES.general;
  
  return {
    redFlag: redFlagAlert, // NEW: Red flag alert data
    insights: topInsights.length > 0 ? topInsights : [{
      key: 'general',
      text: "Your check-in is recorded. Keep monitoring how you feel",
      severity: 'low',
      icon: '📝'
    }],
    actions: uniqueActions.length > 0 ? uniqueActions : [
      { text: 'Continue with your healthy habits', icon: '✨', priority: 1 },
      { text: 'Check in again tomorrow', icon: '📅', priority: 2 }
    ],
    seekHelp: seekHelpGuidelines,
    overallSeverity: topInsights[0]?.severity || 'low'
  };
};

export default generateHealthInsights;
